import * as React from 'react';

import {
  Button,
  Col,
  IconButton,
  Popover,
  PopoverDialog,
  PopoverTrigger,
  Switch,
} from '@rematter/ui-web';
import { useAtom, useSetAtom } from 'jotai';
import {
  LucideHand,
  LucideLayoutGrid,
  LucidePencil,
  LucideSettings,
  LucideSlidersHorizontal,
  LucideZap,
} from 'lucide-react';

import { useTranslations } from '../../../i18n/useTranslations';

import {
  $itemTypeEditMode,
  $quickAddItemsEditMode,
  $scaleTicketShowExtras,
  $scaleTicketTouchMode,
  $touchModalEditMode,
  $touchModalOpen,
} from '~/modules/trade/_state/scaleTicketSettings.state';

//
// ⚛️ ⚛️ ⚛️ SCALE TICKET SETTINGS POPOVER ⚛️ ⚛️ ⚛️
//
export const ScaleTicketSettingsPopover = () => {
  const t = useTranslations();

  const [touchMode, setTouchMode] = useAtom($scaleTicketTouchMode);
  const [showExtras, setShowExtras] = useAtom($scaleTicketShowExtras);
  const setItemTypeEditMode = useSetAtom($itemTypeEditMode);
  const setQuickAddItemsEditMode = useSetAtom($quickAddItemsEditMode);
  const setTouchModalEditMode = useSetAtom($touchModalEditMode);
  const setTouchModalOpen = useSetAtom($touchModalOpen);

  const [isOpen, setIsOpen] = React.useState(false);

  const handleEditItemTypes = React.useCallback(() => {
    setItemTypeEditMode(true);
    setIsOpen(false);
  }, [setItemTypeEditMode]);

  const handleEditQuickAddItems = React.useCallback(() => {
    setQuickAddItemsEditMode(true);
    setIsOpen(false);
  }, [setQuickAddItemsEditMode]);

  const handleCustomizeTouchMode = React.useCallback(() => {
    setTouchModalEditMode(true);
    setTouchModalOpen(true);
    setIsOpen(false);
  }, [setTouchModalEditMode, setTouchModalOpen]);

  return (
    <PopoverTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      <IconButton
        aria-label={t.scaleTicketPro.settings.title}
        icon={<LucideSettings className="size-4" />}
      />

      <Popover placement="bottom end">
        <PopoverDialog className="@ScaleTicketSettingsPopover w-80">
          <Col>
            <div className="border-b border-neutral/10 px-4 py-3">
              <span className="text-sm font-semibold">
                {t.scaleTicketPro.settings.title}
              </span>
            </div>

            <Col gap3 className="border-b border-neutral/10 px-4 py-3">
              <div className="flex items-center gap-1.5 text-neutral/50">
                <LucideSlidersHorizontal className="size-3.5" />
                <span className="text-xs font-medium uppercase">
                  {t.scaleTicketPro.settings.myPreferences}
                </span>
              </div>

              <Col gap2>
                <Switch
                  isSelected={touchMode}
                  onChange={(val: boolean) => setTouchMode(val)}
                >
                  <Col>
                    <span className="text-sm">
                      {t.scaleTicketPro.settings.touchMode}
                    </span>
                    <span className="text-xs text-neutral/50">
                      {t.scaleTicketPro.settings.touchModeDescription}
                    </span>
                  </Col>
                </Switch>

                <Switch
                  isSelected={showExtras}
                  onChange={(val: boolean) => setShowExtras(val)}
                >
                  <Col>
                    <span className="text-sm">
                      {t.scaleTicketPro.settings.showExtras}
                    </span>
                    <span className="text-xs text-neutral/50">
                      {t.scaleTicketPro.settings.showExtrasDescription}
                    </span>
                  </Col>
                </Switch>
              </Col>
            </Col>

            <Col gap1 className="px-4 py-3">
              <div className="flex items-center gap-1.5 text-neutral/50">
                <LucidePencil className="size-3.5" />
                <span className="text-xs font-medium uppercase">
                  {t.scaleTicketPro.settings.organization}
                </span>
              </div>

              <Col>
                <Button
                  variant="text"
                  size="small"
                  className="[&>*]:justify-start"
                  onPress={handleEditItemTypes}
                  icon={<LucideLayoutGrid className="size-4" />}
                >
                  {t.scaleTicketPro.settings.editItemTypes}
                </Button>
                <Button
                  variant="text"
                  size="small"
                  className="[&>*]:justify-start"
                  onPress={handleEditQuickAddItems}
                  icon={<LucideZap className="size-4" />}
                >
                  {t.scaleTicketPro.settings.editQuickAddItems}
                </Button>
                <Button
                  variant="text"
                  size="small"
                  className="[&>*]:justify-start"
                  onPress={handleCustomizeTouchMode}
                  icon={<LucideHand className="size-4" />}
                >
                  {t.scaleTicketPro.settings.customizeTouchMode}
                </Button>
              </Col>
            </Col>
          </Col>
        </PopoverDialog>
      </Popover>
    </PopoverTrigger>
  );
};
